import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import {
  coproducerService,
  CreateCoProducerDTO,
  UpdateCoProducerDTO,
} from '@/services/coproducer.service.ts';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useModalStore from '@/store/useModalStore.ts';
import { AxiosError } from 'axios';
import { AxiosErrorResponse } from '@/@types/Errors.ts';
import { useState } from 'react';

export function useCoproducers() {
  const { t } = useTranslation('coproducer');
  const { id: productId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { closeModal } = useModalStore();
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: [`coproducer-${productId}`, productId, currentPage],
    queryFn: () => coproducerService.getAll(productId, currentPage),
  });

  const coproducers = data?.data || [];
  const pagination = {
    currentPage: data?.meta?.current_page || 1,
    lastPage: data?.meta?.last_page || 1,
    total: data?.meta?.total || 0,
    hasNextPage: !!data?.links?.next,
    hasPrevPage: !!data?.links?.prev,
  };

  const createCoproducer = useMutation({
    mutationFn: (data: CreateCoProducerDTO) =>
      coproducerService.create(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`coproducer-${productId}`] });
      toast({
        description: t('coproducer_created_description'),
        variant: 'success',
      });
      closeModal('invite-co-producer');
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        description: error.response.data.message,
        variant: 'error',
      });
    },
  });

  const updateCoProducer = useMutation({
    mutationFn: async ({
      productInviteId,
      data,
    }: {
      productInviteId: string;
      data: UpdateCoProducerDTO;
    }) => await coproducerService.update(productId, productInviteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`coproducer-${productId}`] });
      toast({
        description: t('coproducer_updated_description'),
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        description: t('coproducer_update_error_description'),
        variant: 'error',
      });
    },
  });

  const deleteCoproducer = useMutation({
    mutationFn: (productInviteId: string) =>
      coproducerService.delete(productId, productInviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`coproducer-${productId}`] });
      toast({
        description: t('coproducer_deleted_description'),
        variant: 'success',
      });
      closeModal('delete-coproducer');
    },
    onError: () => {
      toast({
        description: t('coproducer_delete_error_description'),
        variant: 'error',
      });
    },
  });

  const changePage = (page: number) => {
    setCurrentPage(page);
  };

  return {
    coproducers,
    pagination,
    currentPage,
    changePage,
    isLoading,
    setCurrentPage,
    data,
    createCoproducer: createCoproducer.mutate,
    deleteCoproducer: deleteCoproducer.mutate,
    updateCoProducer: updateCoProducer.mutate,
    isPending:
      createCoproducer.isPending ||
      deleteCoproducer.isPending ||
      updateCoProducer.isPending,
  };
}
